import { Dynamic } from "solid-js/web";
import { cn } from "@bso/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the large wordmark. Defaults to "h1". Pass "p" when the
   * logo is decorative and the page heading already lives elsewhere (e.g. the
   * auth brand surface, where AuthCard owns the single page h1).
   */
  as?: "h1" | "p";
}

/**
 * Marca de Bible Strong Offline.
 *
 * El glifo es un libro abierto con una cruz que se eleva desde el lomo central:
 * "Escritura + estudio" — erudito, reverente y propio. El mark vive SIEMPRE
 * sobre un chip sólido (`bg-accent` + `text-accent-foreground`), nunca sobre un
 * tinte tenue: así contrasta de forma garantizada en CADA tema embarcado
 * (claros con acentos saturados, oscuros, sepia, etc.). El wordmark usa la serif
 * de marca (EB Garamond) SIEMPRE en `text-foreground` (contraste garantizado en
 * todo tema) y distingue "Strong" solo por peso (bold), no por color: así nunca
 * cae bajo el umbral AA como pasaría con `text-accent` en temas como Carbon o
 * Royal Velvet (acento ~2.5:1 sobre su fondo). El color de marca lo aporta el
 * chip sólido del glifo.
 */
function BibleMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* Cruz que se eleva sobre el libro */}
      <path
        d="M12 2.4v4.2M10.3 4h3.4"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
      />
      {/* Libro abierto */}
      <path
        d="M12 9.1C10.3 7.9 8 7.5 5.4 7.8c-.5 0-.8.4-.8.9v9.1c0 .5.4.9.9.8 2.3-.2 4.4.2 5.9 1.3.3.2.7.2 1 0 1.5-1.1 3.6-1.5 5.9-1.3.5 0 .9-.3.9-.8V8.7c0-.5-.4-.9-.8-.9-2.6-.3-4.9.1-6.5 1.3Z"
        fill="currentColor"
        fill-opacity="0.18"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linejoin="round"
      />
      {/* Lomo / canal central */}
      <path d="M12 9.1v11.7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
      {/* Renglones de escritura (páginas) */}
      <path
        d="M7 11.4c1.4-.2 2.6 0 3.4.5M7 14c1.4-.2 2.6 0 3.4.5M13.6 11.9c.8-.5 2-.7 3.4-.5M13.6 14.5c.8-.5 2-.7 3.4-.5"
        stroke="currentColor"
        stroke-width="1.1"
        stroke-linecap="round"
        stroke-opacity="0.65"
      />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="h-9 w-9 rounded-xl bg-accent text-accent-foreground shadow-sm flex items-center justify-center shrink-0">
            <BibleMark class="h-5 w-5" />
          </div>
          <span class="font-serif text-xl tracking-tight text-foreground">
            Bible<span class="font-semibold">Strong</span>
          </span>
        </>
      ) : (
        <>
          <div class="h-16 w-16 rounded-2xl bg-accent text-accent-foreground flex items-center justify-center mb-1 shadow-md shrink-0 transition-transform hover:scale-105 active:scale-95 duration-300">
            <BibleMark class="h-9 w-9" />
          </div>
          <Dynamic
            component={props.as ?? "h1"}
            class="font-serif text-3xl tracking-tight text-foreground"
          >
            Bible<span class="font-semibold">Strong</span>
          </Dynamic>
        </>
      )}
    </div>
  );
}
